import { createContext, useContext, useReducer, ReactNode, useMemo, useCallback, useRef, useEffect } from 'react';
import { subHours, format } from 'date-fns';
import cronstrue from 'cronstrue';

import {
  listLocations,
  getLocation,
  testLocationConnectivity,
  transformLocationToDisplay,
  type LocationDisplay,
  type ListLocationsParams,
  type Location,
  type TestConnectivityResponse,
} from '../api/Locations';
import { listCredentials, getCredential, type Credential } from '../api/Credentials';
import {
  listScheduledDeliveries,
  getScheduledDelivery,
  transformScheduledDeliveryToDisplay,
  type ScheduledDeliveryDisplay,
  type ListScheduledDeliveriesParams,
} from '../api/ScheduledDeliveries';
import {
  listDeliveries,
  getDelivery,
  transformDeliveryToDisplay,
  type DeliveryDisplay,
  type ListDeliveriesParams,
} from '../api/Deliveries';
import {
  listServiceAccounts,
  getServiceAccount,
  rotateServiceAccountPassword,
  type ServiceAccount,
  type ListServiceAccountsParams,
} from '../api/ServiceAccounts';
import type { PaginationParams, ApiContext } from '../api/shared';
export type { ApiContext };

export interface FileDeliveryError {
  id: string;
  message: string;
  type: 'error' | 'warning';
  dismissible?: boolean;
}

export type ViewMode = 'list' | 'grid';

export type SortDirection = 'asc' | 'desc';

export type DeliveriesSortField = 'end_time' | 'state';

export type LocationsSortField = 'display_name' | 'name' | 'create_time' | 'update_time' | 'uri';

export type CredentialsSortField = 'name' | 'display_name' | 'create_time' | 'update_time' | 'credential_type';

export type SchedulesSortField =
  | 'display_name'
  | 'name'
  | 'create_time'
  | 'update_time'
  | 'next_execution_time'
  | 'state';

export type ServiceAccountsSortField = 'display_name' | 'username' | 'create_time' | 'update_time';

interface PaginationState {
  nextPageToken?: string;
  currentPageToken?: string;
  pageTokenHistory: string[];
  currentPage: number;
  totalSize: number;
  pageSize: number;
  filter?: string;
}

interface FileDeliveryState {
  // Errors
  errors: FileDeliveryError[];

  // Locations
  locations: LocationDisplay[];
  locationsLoading: boolean;
  currentLocation: LocationDisplay | null;
  locationLoading: boolean;
  locationsPagination: PaginationState;
  locationsActiveSearchName?: string;

  // Credentials (Credentials)
  credentials: Credential[];
  credentialsLoading: boolean;
  currentCredential: Credential | null;
  credentialLoading: boolean;
  credentialsPagination: PaginationState;
  credentialsSortField?: CredentialsSortField;
  credentialsSortDirection?: SortDirection;
  credentialsActiveSearchName?: string;

  // Schedules
  schedules: ScheduledDeliveryDisplay[];
  schedulesLoading: boolean;
  currentSchedule: ScheduledDeliveryDisplay | null;
  scheduleLoading: boolean;
  schedulesPagination: PaginationState;
  schedulesSortField?: SchedulesSortField;
  schedulesSortDirection?: SortDirection;
  schedulesActiveSearchName?: string;

  // Deliveries
  deliveries: DeliveryDisplay[];
  deliveriesLoading: boolean;
  currentDelivery: DeliveryDisplay | null;
  deliveryLoading: boolean;
  deliveriesPagination: PaginationState;
  deliveriesSortField?: DeliveriesSortField;
  deliveriesSortDirection?: SortDirection;
  deliveriesActiveSearchName?: string;

  // Locations sorting
  locationsSortField?: LocationsSortField;
  locationsSortDirection?: SortDirection;

  // Shared view mode (used across all lists)
  sharedViewMode: ViewMode;

  // Service Accounts
  serviceAccounts: ServiceAccount[];
  serviceAccountsLoading: boolean;
  currentServiceAccount: ServiceAccount | null;
  serviceAccountLoading: boolean;
  serviceAccountSaving: boolean;
  serviceAccountSaveSuccess: boolean;
  serviceAccountsSortField?: ServiceAccountsSortField;
  serviceAccountsSortDirection?: SortDirection;
  serviceAccountsPagination: PaginationState;
  serviceAccountsActiveSearchName?: string;

  // Shared reference data (for dropdowns)
  allLocations: Location[];
  allCredentials: Credential[];

  // Service Account Selector (for the header selector)
  // Stores the service account name (e.g., 'epadmin'), not the uid
  selectedServiceAccount?: string;

  // Data Region Selector (for the header selector)
  selectedDataRegion?: string;

  // Initialization flags
  dataRegionInitialized: boolean;
  serviceAccountInitialized: boolean;
}

type FileDeliveryAction =
  // Error actions
  | { type: 'ADD_ERROR'; payload: FileDeliveryError }
  | { type: 'REMOVE_ERROR'; payload: string }
  | { type: 'CLEAR_ALL_ERRORS' }

  // Location actions
  | { type: 'FETCH_LOCATIONS_START' }
  | {
      type: 'FETCH_LOCATIONS_SUCCESS';
      payload: {
        locations: LocationDisplay[];
        allCredentials: Credential[];
        nextPageToken?: string;
        totalSize: number;
      };
    }
  | { type: 'FETCH_LOCATIONS_ERROR' }
  | { type: 'FETCH_LOCATION_START' }
  | { type: 'FETCH_LOCATION_SUCCESS'; payload: { location: LocationDisplay; allCredentials: Credential[] } }
  | { type: 'FETCH_LOCATION_ERROR' }
  | { type: 'CLEAR_CURRENT_LOCATION' }
  | { type: 'SET_LOCATIONS_PAGE_SIZE'; payload: number }
  | { type: 'SET_LOCATIONS_FILTER'; payload: string | undefined }
  | { type: 'SET_LOCATIONS_SORT'; payload: { field?: LocationsSortField; direction?: SortDirection } }
  | { type: 'LOCATIONS_GO_TO_NEXT_PAGE' }
  | { type: 'LOCATIONS_GO_TO_PREVIOUS_PAGE' }
  | { type: 'LOCATIONS_GO_TO_FIRST_PAGE' }
  | { type: 'SET_LOCATIONS_ACTIVE_SEARCH'; payload: string | undefined }

  // Credential actions
  | { type: 'FETCH_CREDENTIALS_START' }
  | {
      type: 'FETCH_CREDENTIALS_SUCCESS';
      payload: { credentials: Credential[]; nextPageToken?: string; totalSize: number };
    }
  | { type: 'FETCH_CREDENTIALS_ERROR' }
  | { type: 'FETCH_CREDENTIAL_START' }
  | { type: 'FETCH_CREDENTIAL_SUCCESS'; payload: Credential }
  | { type: 'FETCH_CREDENTIAL_ERROR' }
  | { type: 'CLEAR_CURRENT_CREDENTIAL' }
  | { type: 'SET_CREDENTIALS_PAGE_SIZE'; payload: number }
  | { type: 'SET_CREDENTIALS_FILTER'; payload: string | undefined }
  | { type: 'SET_CREDENTIALS_SORT'; payload: { field?: CredentialsSortField; direction?: SortDirection } }
  | { type: 'CREDENTIALS_GO_TO_NEXT_PAGE' }
  | { type: 'CREDENTIALS_GO_TO_PREVIOUS_PAGE' }
  | { type: 'CREDENTIALS_GO_TO_FIRST_PAGE' }
  | { type: 'SET_CREDENTIALS_ACTIVE_SEARCH'; payload: string | undefined }

  // Schedule actions
  | { type: 'FETCH_SCHEDULES_START' }
  | {
      type: 'FETCH_SCHEDULES_SUCCESS';
      payload: {
        schedules: ScheduledDeliveryDisplay[];
        allLocations: Location[];
        nextPageToken?: string;
        totalSize: number;
      };
    }
  | { type: 'FETCH_SCHEDULES_ERROR' }
  | { type: 'FETCH_SCHEDULE_START' }
  | { type: 'FETCH_SCHEDULE_SUCCESS'; payload: { schedule: ScheduledDeliveryDisplay; allLocations: Location[] } }
  | { type: 'FETCH_SCHEDULE_ERROR' }
  | { type: 'CLEAR_CURRENT_SCHEDULE' }
  | { type: 'SET_SCHEDULES_PAGE_SIZE'; payload: number }
  | { type: 'SET_SCHEDULES_FILTER'; payload: string | undefined }
  | { type: 'SET_SCHEDULES_SORT'; payload: { field?: SchedulesSortField; direction?: SortDirection } }
  | { type: 'SCHEDULES_GO_TO_NEXT_PAGE' }
  | { type: 'SCHEDULES_GO_TO_PREVIOUS_PAGE' }
  | { type: 'SCHEDULES_GO_TO_FIRST_PAGE' }
  | { type: 'SET_SCHEDULES_ACTIVE_SEARCH'; payload: string | undefined }

  // Delivery actions
  | { type: 'FETCH_DELIVERIES_START' }
  | {
      type: 'FETCH_DELIVERIES_SUCCESS';
      payload: { deliveries: DeliveryDisplay[]; allLocations: Location[]; nextPageToken?: string; totalSize: number };
    }
  | { type: 'FETCH_DELIVERIES_ERROR' }
  | { type: 'FETCH_DELIVERY_START' }
  | { type: 'FETCH_DELIVERY_SUCCESS'; payload: { delivery: DeliveryDisplay; allLocations: Location[] } }
  | { type: 'FETCH_DELIVERY_ERROR' }
  | { type: 'CLEAR_CURRENT_DELIVERY' }
  | { type: 'SET_DELIVERIES_PAGE_SIZE'; payload: number }
  | { type: 'SET_DELIVERIES_FILTER'; payload: string | undefined }
  | { type: 'SET_DELIVERIES_SORT'; payload: { field?: DeliveriesSortField; direction?: SortDirection } }
  | { type: 'SET_SHARED_VIEW_MODE'; payload: ViewMode }
  | { type: 'DELIVERIES_GO_TO_NEXT_PAGE' }
  | { type: 'DELIVERIES_GO_TO_PREVIOUS_PAGE' }
  | { type: 'DELIVERIES_GO_TO_FIRST_PAGE' }
  | { type: 'SET_DELIVERIES_ACTIVE_SEARCH'; payload: string | undefined }

  // Service Account actions
  | { type: 'FETCH_SERVICE_ACCOUNTS_START' }
  | {
      type: 'FETCH_SERVICE_ACCOUNTS_SUCCESS';
      payload: { accounts: ServiceAccount[]; nextPageToken?: string; totalSize: number };
    }
  | { type: 'FETCH_SERVICE_ACCOUNTS_ERROR' }
  | { type: 'FETCH_SERVICE_ACCOUNT_START' }
  | { type: 'FETCH_SERVICE_ACCOUNT_SUCCESS'; payload: ServiceAccount }
  | { type: 'FETCH_SERVICE_ACCOUNT_ERROR' }
  | { type: 'CLEAR_CURRENT_SERVICE_ACCOUNT' }
  | { type: 'SET_SERVICE_ACCOUNTS_PAGE_SIZE'; payload: number }
  | { type: 'SET_SERVICE_ACCOUNTS_FILTER'; payload: string | undefined }
  | { type: 'SET_SERVICE_ACCOUNTS_SORT'; payload: { field?: ServiceAccountsSortField; direction?: SortDirection } }
  | { type: 'SERVICE_ACCOUNTS_GO_TO_NEXT_PAGE' }
  | { type: 'SERVICE_ACCOUNTS_GO_TO_PREVIOUS_PAGE' }
  | { type: 'SERVICE_ACCOUNTS_GO_TO_FIRST_PAGE' }
  | { type: 'UPDATE_PASSWORD_START' }
  | { type: 'UPDATE_PASSWORD_SUCCESS' }
  | { type: 'UPDATE_PASSWORD_ERROR' }
  | { type: 'CLEAR_SAVE_STATUS' }
  | { type: 'SET_SERVICE_ACCOUNTS_ACTIVE_SEARCH'; payload: string | undefined }

  // Shared data actions
  | { type: 'SET_ALL_LOCATIONS'; payload: Location[] }
  | { type: 'SET_ALL_CREDENTIALS'; payload: Credential[] }

  // Service Account Selector actions
  | { type: 'SET_SELECTED_SERVICE_ACCOUNT'; payload: string | undefined }

  // Data Region Selector actions
  | { type: 'SET_SELECTED_DATA_REGION'; payload: string | undefined }

  // Initialization actions
  | { type: 'SET_DATA_REGION_INITIALIZED' }
  | { type: 'SET_SERVICE_ACCOUNT_INITIALIZED' }

  // Reset all data when context changes
  | { type: 'RESET_ALL_DATA' };

const createInitialPaginationState = (pageSize = 10): PaginationState => ({
  nextPageToken: undefined,
  currentPageToken: undefined,
  pageTokenHistory: [],
  currentPage: 1,
  totalSize: 0,
  pageSize,
  filter: undefined,
});

const initialState: FileDeliveryState = {
  errors: [],

  locations: [],
  locationsLoading: false,
  currentLocation: null,
  locationLoading: false,
  locationsPagination: createInitialPaginationState(10),

  credentials: [],
  credentialsLoading: false,
  currentCredential: null,
  credentialLoading: false,
  credentialsPagination: createInitialPaginationState(6),
  credentialsSortField: 'display_name',
  credentialsSortDirection: 'asc',

  schedules: [],
  schedulesLoading: false,
  currentSchedule: null,
  scheduleLoading: false,
  schedulesPagination: createInitialPaginationState(10),
  schedulesSortField: 'display_name',
  schedulesSortDirection: 'asc',

  deliveries: [],
  deliveriesLoading: false,
  currentDelivery: null,
  deliveryLoading: false,
  deliveriesPagination: {
    ...createInitialPaginationState(10),
    filter: `end_time > "${format(subHours(new Date(), 24), "yyyy-MM-dd'T'HH:mm:ss'Z'")}"`,
  },
  deliveriesSortField: 'end_time',
  deliveriesSortDirection: 'desc',
  deliveriesActiveSearchName: 'Default',

  locationsSortField: 'display_name',
  locationsSortDirection: 'asc',

  sharedViewMode: 'list',

  serviceAccounts: [],
  serviceAccountsLoading: false,
  currentServiceAccount: null,
  serviceAccountLoading: false,
  serviceAccountSaving: false,
  serviceAccountSaveSuccess: false,
  serviceAccountsSortField: 'display_name',
  serviceAccountsSortDirection: 'asc',
  serviceAccountsPagination: createInitialPaginationState(10),

  allLocations: [],
  allCredentials: [],

  selectedServiceAccount: undefined,

  selectedDataRegion: undefined,

  dataRegionInitialized: false,
  serviceAccountInitialized: false,
};

const handlePaginationNextPage = (pagination: PaginationState): PaginationState => ({
  ...pagination,
  currentPage: pagination.currentPage + 1,
  pageTokenHistory: pagination.currentPageToken
    ? [...pagination.pageTokenHistory, pagination.currentPageToken]
    : pagination.pageTokenHistory,
  currentPageToken: pagination.nextPageToken,
});

const handlePaginationPreviousPage = (pagination: PaginationState): PaginationState => {
  const newHistory = [...pagination.pageTokenHistory];
  const previousToken = newHistory.pop();
  return {
    ...pagination,
    currentPage: pagination.currentPage - 1,
    pageTokenHistory: newHistory,
    currentPageToken: previousToken,
  };
};

const handlePaginationFirstPage = (pagination: PaginationState): PaginationState => ({
  ...pagination,
  currentPage: 1,
  currentPageToken: undefined,
  pageTokenHistory: [],
});

const handlePaginationSetPageSize = (pagination: PaginationState, pageSize: number): PaginationState => ({
  ...pagination,
  pageSize,
  currentPage: 1,
  currentPageToken: undefined,
  pageTokenHistory: [],
});

const handlePaginationSetFilter = (pagination: PaginationState, filter: string | undefined): PaginationState => ({
  ...pagination,
  filter,
  currentPage: 1,
  currentPageToken: undefined,
  pageTokenHistory: [],
});

const fileDeliveryReducer = (state: FileDeliveryState, action: FileDeliveryAction): FileDeliveryState => {
  switch (action.type) {
    // Error actions
    case 'ADD_ERROR':
      return {
        ...state,
        errors: [...state.errors.filter((e) => e.id !== action.payload.id), action.payload],
      };
    case 'REMOVE_ERROR':
      return {
        ...state,
        errors: state.errors.filter((e) => e.id !== action.payload),
      };
    case 'CLEAR_ALL_ERRORS':
      return { ...state, errors: [] };

    // Location actions
    case 'FETCH_LOCATIONS_START':
      return { ...state, locationsLoading: true };
    case 'FETCH_LOCATIONS_SUCCESS':
      return {
        ...state,
        locationsLoading: false,
        locations: action.payload.locations,
        allCredentials: action.payload.allCredentials,
        locationsPagination: {
          ...state.locationsPagination,
          nextPageToken: action.payload.nextPageToken,
          totalSize: action.payload.totalSize,
        },
      };
    case 'FETCH_LOCATIONS_ERROR':
      return { ...state, locationsLoading: false };
    case 'FETCH_LOCATION_START':
      return { ...state, locationLoading: true, currentLocation: null };
    case 'FETCH_LOCATION_SUCCESS':
      return {
        ...state,
        locationLoading: false,
        currentLocation: action.payload.location,
        allCredentials: action.payload.allCredentials,
      };
    case 'FETCH_LOCATION_ERROR':
      return { ...state, locationLoading: false, currentLocation: null };
    case 'CLEAR_CURRENT_LOCATION':
      return { ...state, currentLocation: null };
    case 'SET_LOCATIONS_PAGE_SIZE':
      return { ...state, locationsPagination: handlePaginationSetPageSize(state.locationsPagination, action.payload) };
    case 'SET_LOCATIONS_FILTER':
      return { ...state, locationsPagination: handlePaginationSetFilter(state.locationsPagination, action.payload) };
    case 'LOCATIONS_GO_TO_NEXT_PAGE':
      return { ...state, locationsPagination: handlePaginationNextPage(state.locationsPagination) };
    case 'LOCATIONS_GO_TO_PREVIOUS_PAGE':
      return { ...state, locationsPagination: handlePaginationPreviousPage(state.locationsPagination) };
    case 'LOCATIONS_GO_TO_FIRST_PAGE':
      return { ...state, locationsPagination: handlePaginationFirstPage(state.locationsPagination) };
    case 'SET_LOCATIONS_SORT':
      return {
        ...state,
        locationsSortField: action.payload.field,
        locationsSortDirection: action.payload.direction,
        locationsPagination: handlePaginationFirstPage(state.locationsPagination),
      };
    case 'SET_LOCATIONS_ACTIVE_SEARCH':
      return { ...state, locationsActiveSearchName: action.payload };

    // Credential actions
    case 'FETCH_CREDENTIALS_START':
      return { ...state, credentialsLoading: true };
    case 'FETCH_CREDENTIALS_SUCCESS':
      return {
        ...state,
        credentialsLoading: false,
        credentials: action.payload.credentials,
        allCredentials: action.payload.credentials,
        credentialsPagination: {
          ...state.credentialsPagination,
          nextPageToken: action.payload.nextPageToken,
          totalSize: action.payload.totalSize,
        },
      };
    case 'FETCH_CREDENTIALS_ERROR':
      return { ...state, credentialsLoading: false };
    case 'FETCH_CREDENTIAL_START':
      return { ...state, credentialLoading: true, currentCredential: null };
    case 'FETCH_CREDENTIAL_SUCCESS':
      return { ...state, credentialLoading: false, currentCredential: action.payload };
    case 'FETCH_CREDENTIAL_ERROR':
      return { ...state, credentialLoading: false, currentCredential: null };
    case 'CLEAR_CURRENT_CREDENTIAL':
      return { ...state, currentCredential: null };
    case 'SET_CREDENTIALS_PAGE_SIZE':
      return {
        ...state,
        credentialsPagination: handlePaginationSetPageSize(state.credentialsPagination, action.payload),
      };
    case 'SET_CREDENTIALS_FILTER':
      return {
        ...state,
        credentialsPagination: handlePaginationSetFilter(state.credentialsPagination, action.payload),
      };
    case 'CREDENTIALS_GO_TO_NEXT_PAGE':
      return { ...state, credentialsPagination: handlePaginationNextPage(state.credentialsPagination) };
    case 'CREDENTIALS_GO_TO_PREVIOUS_PAGE':
      return { ...state, credentialsPagination: handlePaginationPreviousPage(state.credentialsPagination) };
    case 'CREDENTIALS_GO_TO_FIRST_PAGE':
      return { ...state, credentialsPagination: handlePaginationFirstPage(state.credentialsPagination) };
    case 'SET_CREDENTIALS_SORT':
      return {
        ...state,
        credentialsSortField: action.payload.field,
        credentialsSortDirection: action.payload.direction,
        credentialsPagination: handlePaginationFirstPage(state.credentialsPagination),
      };
    case 'SET_CREDENTIALS_ACTIVE_SEARCH':
      return { ...state, credentialsActiveSearchName: action.payload };

    // Schedule actions
    case 'FETCH_SCHEDULES_START':
      return { ...state, schedulesLoading: true };
    case 'FETCH_SCHEDULES_SUCCESS':
      return {
        ...state,
        schedulesLoading: false,
        schedules: action.payload.schedules,
        allLocations: action.payload.allLocations,
        schedulesPagination: {
          ...state.schedulesPagination,
          nextPageToken: action.payload.nextPageToken,
          totalSize: action.payload.totalSize,
        },
      };
    case 'FETCH_SCHEDULES_ERROR':
      return { ...state, schedulesLoading: false };
    case 'FETCH_SCHEDULE_START':
      return { ...state, scheduleLoading: true, currentSchedule: null };
    case 'FETCH_SCHEDULE_SUCCESS':
      return {
        ...state,
        scheduleLoading: false,
        currentSchedule: action.payload.schedule,
        allLocations: action.payload.allLocations,
      };
    case 'FETCH_SCHEDULE_ERROR':
      return { ...state, scheduleLoading: false, currentSchedule: null };
    case 'CLEAR_CURRENT_SCHEDULE':
      return { ...state, currentSchedule: null };
    case 'SET_SCHEDULES_PAGE_SIZE':
      return { ...state, schedulesPagination: handlePaginationSetPageSize(state.schedulesPagination, action.payload) };
    case 'SET_SCHEDULES_FILTER':
      return { ...state, schedulesPagination: handlePaginationSetFilter(state.schedulesPagination, action.payload) };
    case 'SCHEDULES_GO_TO_NEXT_PAGE':
      return { ...state, schedulesPagination: handlePaginationNextPage(state.schedulesPagination) };
    case 'SCHEDULES_GO_TO_PREVIOUS_PAGE':
      return { ...state, schedulesPagination: handlePaginationPreviousPage(state.schedulesPagination) };
    case 'SCHEDULES_GO_TO_FIRST_PAGE':
      return { ...state, schedulesPagination: handlePaginationFirstPage(state.schedulesPagination) };
    case 'SET_SCHEDULES_SORT':
      return {
        ...state,
        schedulesSortField: action.payload.field,
        schedulesSortDirection: action.payload.direction,
        schedulesPagination: handlePaginationFirstPage(state.schedulesPagination),
      };
    case 'SET_SCHEDULES_ACTIVE_SEARCH':
      return { ...state, schedulesActiveSearchName: action.payload };

    // Delivery actions
    case 'FETCH_DELIVERIES_START':
      return { ...state, deliveriesLoading: true };
    case 'FETCH_DELIVERIES_SUCCESS':
      return {
        ...state,
        deliveriesLoading: false,
        deliveries: action.payload.deliveries,
        allLocations: action.payload.allLocations,
        deliveriesPagination: {
          ...state.deliveriesPagination,
          nextPageToken: action.payload.nextPageToken,
          totalSize: action.payload.totalSize,
        },
      };
    case 'FETCH_DELIVERIES_ERROR':
      return { ...state, deliveriesLoading: false };
    case 'FETCH_DELIVERY_START':
      return { ...state, deliveryLoading: true, currentDelivery: null };
    case 'FETCH_DELIVERY_SUCCESS':
      return {
        ...state,
        deliveryLoading: false,
        currentDelivery: action.payload.delivery,
        allLocations: action.payload.allLocations,
      };
    case 'FETCH_DELIVERY_ERROR':
      return { ...state, deliveryLoading: false, currentDelivery: null };
    case 'CLEAR_CURRENT_DELIVERY':
      return { ...state, currentDelivery: null };
    case 'SET_DELIVERIES_PAGE_SIZE':
      return {
        ...state,
        deliveriesPagination: handlePaginationSetPageSize(state.deliveriesPagination, action.payload),
      };
    case 'SET_DELIVERIES_FILTER':
      return { ...state, deliveriesPagination: handlePaginationSetFilter(state.deliveriesPagination, action.payload) };
    case 'SET_DELIVERIES_SORT':
      return {
        ...state,
        deliveriesSortField: action.payload.field,
        deliveriesSortDirection: action.payload.direction,
        deliveriesPagination: handlePaginationFirstPage(state.deliveriesPagination),
      };
    case 'DELIVERIES_GO_TO_NEXT_PAGE':
      return { ...state, deliveriesPagination: handlePaginationNextPage(state.deliveriesPagination) };
    case 'DELIVERIES_GO_TO_PREVIOUS_PAGE':
      return { ...state, deliveriesPagination: handlePaginationPreviousPage(state.deliveriesPagination) };
    case 'DELIVERIES_GO_TO_FIRST_PAGE':
      return { ...state, deliveriesPagination: handlePaginationFirstPage(state.deliveriesPagination) };
    case 'SET_SHARED_VIEW_MODE':
      return { ...state, sharedViewMode: action.payload };
    case 'SET_DELIVERIES_ACTIVE_SEARCH':
      return { ...state, deliveriesActiveSearchName: action.payload };

    // Service Account actions
    case 'FETCH_SERVICE_ACCOUNTS_START':
      return { ...state, serviceAccountsLoading: true };
    case 'FETCH_SERVICE_ACCOUNTS_SUCCESS':
      return {
        ...state,
        serviceAccountsLoading: false,
        serviceAccounts: action.payload.accounts,
        serviceAccountsPagination: {
          ...state.serviceAccountsPagination,
          nextPageToken: action.payload.nextPageToken,
          totalSize: action.payload.totalSize,
        },
      };
    case 'FETCH_SERVICE_ACCOUNTS_ERROR':
      return { ...state, serviceAccountsLoading: false };
    case 'FETCH_SERVICE_ACCOUNT_START':
      return { ...state, serviceAccountLoading: true, currentServiceAccount: null };
    case 'FETCH_SERVICE_ACCOUNT_SUCCESS':
      return { ...state, serviceAccountLoading: false, currentServiceAccount: action.payload };
    case 'FETCH_SERVICE_ACCOUNT_ERROR':
      return { ...state, serviceAccountLoading: false, currentServiceAccount: null };
    case 'CLEAR_CURRENT_SERVICE_ACCOUNT':
      return { ...state, currentServiceAccount: null, serviceAccountSaveSuccess: false };
    case 'SET_SERVICE_ACCOUNTS_PAGE_SIZE':
      return {
        ...state,
        serviceAccountsPagination: handlePaginationSetPageSize(state.serviceAccountsPagination, action.payload),
      };
    case 'SET_SERVICE_ACCOUNTS_FILTER':
      return {
        ...state,
        serviceAccountsPagination: handlePaginationSetFilter(state.serviceAccountsPagination, action.payload),
      };
    case 'SERVICE_ACCOUNTS_GO_TO_NEXT_PAGE':
      return { ...state, serviceAccountsPagination: handlePaginationNextPage(state.serviceAccountsPagination) };
    case 'SERVICE_ACCOUNTS_GO_TO_PREVIOUS_PAGE':
      return { ...state, serviceAccountsPagination: handlePaginationPreviousPage(state.serviceAccountsPagination) };
    case 'SERVICE_ACCOUNTS_GO_TO_FIRST_PAGE':
      return { ...state, serviceAccountsPagination: handlePaginationFirstPage(state.serviceAccountsPagination) };
    case 'SET_SERVICE_ACCOUNTS_SORT':
      return {
        ...state,
        serviceAccountsSortField: action.payload.field,
        serviceAccountsSortDirection: action.payload.direction,
        serviceAccountsPagination: handlePaginationFirstPage(state.serviceAccountsPagination),
      };
    case 'UPDATE_PASSWORD_START':
      return { ...state, serviceAccountSaving: true, serviceAccountSaveSuccess: false };
    case 'UPDATE_PASSWORD_SUCCESS':
      return { ...state, serviceAccountSaving: false, serviceAccountSaveSuccess: true };
    case 'UPDATE_PASSWORD_ERROR':
      return { ...state, serviceAccountSaving: false, serviceAccountSaveSuccess: false };
    case 'CLEAR_SAVE_STATUS':
      return { ...state, serviceAccountSaveSuccess: false };
    case 'SET_SERVICE_ACCOUNTS_ACTIVE_SEARCH':
      return { ...state, serviceAccountsActiveSearchName: action.payload };

    // Shared data actions
    case 'SET_ALL_LOCATIONS':
      return { ...state, allLocations: action.payload };
    case 'SET_ALL_CREDENTIALS':
      return { ...state, allCredentials: action.payload };

    // Service Account Selector actions
    case 'SET_SELECTED_SERVICE_ACCOUNT':
      return { ...state, selectedServiceAccount: action.payload };

    // Data Region Selector actions
    case 'SET_SELECTED_DATA_REGION':
      return { ...state, selectedDataRegion: action.payload };

    // Initialization actions
    case 'SET_DATA_REGION_INITIALIZED':
      return { ...state, dataRegionInitialized: true };
    case 'SET_SERVICE_ACCOUNT_INITIALIZED':
      return { ...state, serviceAccountInitialized: true };

    // Reset all data when context changes
    case 'RESET_ALL_DATA':
      return {
        ...state,
        locations: [],
        locationsLoading: false,
        currentLocation: null,
        locationLoading: false,
        locationsPagination: createInitialPaginationState(state.locationsPagination.pageSize),

        credentials: [],
        credentialsLoading: false,
        currentCredential: null,
        credentialLoading: false,
        credentialsPagination: createInitialPaginationState(state.credentialsPagination.pageSize),

        schedules: [],
        schedulesLoading: false,
        currentSchedule: null,
        scheduleLoading: false,
        schedulesPagination: createInitialPaginationState(state.schedulesPagination.pageSize),

        deliveries: [],
        deliveriesLoading: false,
        currentDelivery: null,
        deliveryLoading: false,
        deliveriesPagination: createInitialPaginationState(state.deliveriesPagination.pageSize),

        serviceAccounts: [],
        serviceAccountsLoading: false,
        currentServiceAccount: null,
        serviceAccountLoading: false,
        serviceAccountsPagination: createInitialPaginationState(state.serviceAccountsPagination.pageSize),

        allLocations: [],
        allCredentials: [],
      };

    default:
      return state;
  }
};

interface FileDeliveryContextValue {
  state: FileDeliveryState;
  dispatch: React.Dispatch<FileDeliveryAction>;
  serviceAccountSelectorRef: React.RefObject<HTMLButtonElement>;
}

const FileDeliveryContext = createContext<FileDeliveryContextValue | null>(null);

interface FileDeliveryProviderProps {
  children: ReactNode;
}

export const FileDeliveryProvider = ({ children }: FileDeliveryProviderProps) => {
  const [state, dispatch] = useReducer(fileDeliveryReducer, initialState);
  const serviceAccountSelectorRef = useRef<HTMLButtonElement>(null);

  const value = useMemo(() => ({ state, dispatch, serviceAccountSelectorRef }), [state]);

  return <FileDeliveryContext.Provider value={value}>{children}</FileDeliveryContext.Provider>;
};

const useFileDeliveryContext = () => {
  const context = useContext(FileDeliveryContext);
  if (!context) {
    throw new Error('useFileDeliveryContext must be used within a FileDeliveryProvider');
  }

  return context;
};

// ==================== API Context Hook ====================

export const useApiContext = (): ApiContext => {
  const { state } = useFileDeliveryContext();

  return useMemo(
    () => ({
      datacenter: state.selectedDataRegion ?? '',
      serviceAccount: state.selectedServiceAccount ?? '',
    }),
    [state.selectedDataRegion, state.selectedServiceAccount],
  );
};

// ==================== Error Hooks ====================

export const useErrorState = () => {
  const { state, dispatch } = useFileDeliveryContext();

  const addError = useCallback(
    (error: Omit<FileDeliveryError, 'id'> & { id?: string }) => {
      const id = error.id || crypto.randomUUID();
      dispatch({
        type: 'ADD_ERROR',
        payload: { ...error, id, dismissible: error.dismissible ?? true },
      });
      return id;
    },
    [dispatch],
  );

  const removeError = useCallback(
    (id: string) => {
      dispatch({ type: 'REMOVE_ERROR', payload: id });
    },
    [dispatch],
  );

  const clearAllErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  }, [dispatch]);

  return {
    errors: state.errors,
    addError,
    removeError,
    clearAllErrors,
  };
};

// ==================== Locations Hooks ====================

export const useLocationsState = () => {
  const { state, dispatch } = useFileDeliveryContext();
  const { addError } = useErrorState();
  const apiContext = useApiContext();

  const orderBy =
    state.locationsSortField && state.locationsSortDirection
      ? `${state.locationsSortField} ${state.locationsSortDirection}`
      : undefined;

  const fetchLocations = useCallback(
    async (params?: ListLocationsParams) => {
      dispatch({ type: 'FETCH_LOCATIONS_START' });
      try {
        const [locationsResponse, credentialsResponse] = await Promise.all([
          listLocations(apiContext, {
            pageSize: state.locationsPagination.pageSize,
            pageToken: state.locationsPagination.currentPageToken,
            filter: state.locationsPagination.filter,
            orderBy,
            ...params,
          }),
          listCredentials(apiContext),
        ]);

        const displayLocations = locationsResponse.locations.map((location) =>
          transformLocationToDisplay(location, credentialsResponse.credentials),
        );

        dispatch({
          type: 'FETCH_LOCATIONS_SUCCESS',
          payload: {
            locations: displayLocations,
            allCredentials: credentialsResponse.credentials,
            nextPageToken: locationsResponse.nextPageToken,
            totalSize: locationsResponse.totalSize,
          },
        });
      } catch {
        dispatch({ type: 'FETCH_LOCATIONS_ERROR' });
        addError({ id: 'fetch-locations-error', message: 'Failed to load locations', type: 'error' });
      }
    },
    [
      dispatch,
      state.locationsPagination.pageSize,
      state.locationsPagination.currentPageToken,
      state.locationsPagination.filter,
      orderBy,
      addError,
      apiContext,
    ],
  );

  const fetchLocation = useCallback(
    async (locationId: string) => {
      dispatch({ type: 'FETCH_LOCATION_START' });
      try {
        const [location, credentialsResponse] = await Promise.all([
          getLocation(apiContext, locationId),
          listCredentials(apiContext),
        ]);
        if (location) {
          const displayLocation = transformLocationToDisplay(location, credentialsResponse.credentials);
          dispatch({
            type: 'FETCH_LOCATION_SUCCESS',
            payload: { location: displayLocation, allCredentials: credentialsResponse.credentials },
          });
        } else {
          dispatch({ type: 'FETCH_LOCATION_ERROR' });
          addError({ id: 'fetch-location-error', message: 'Location not found', type: 'error' });
        }
      } catch {
        dispatch({ type: 'FETCH_LOCATION_ERROR' });
        addError({ id: 'fetch-location-error', message: 'Failed to load location', type: 'error' });
      }
    },
    [dispatch, addError, apiContext],
  );

  const clearCurrentLocation = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_LOCATION' });
  }, [dispatch]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch({ type: 'SET_LOCATIONS_PAGE_SIZE', payload: pageSize });
    },
    [dispatch],
  );

  const setFilter = useCallback(
    (filter: string | undefined) => {
      dispatch({ type: 'SET_LOCATIONS_FILTER', payload: filter });
    },
    [dispatch],
  );

  const goToNextPage = useCallback(() => {
    dispatch({ type: 'LOCATIONS_GO_TO_NEXT_PAGE' });
  }, [dispatch]);

  const goToPreviousPage = useCallback(() => {
    dispatch({ type: 'LOCATIONS_GO_TO_PREVIOUS_PAGE' });
  }, [dispatch]);

  const goToFirstPage = useCallback(() => {
    dispatch({ type: 'LOCATIONS_GO_TO_FIRST_PAGE' });
  }, [dispatch]);

  const setSort = useCallback(
    (field?: LocationsSortField, direction?: SortDirection) => {
      dispatch({ type: 'SET_LOCATIONS_SORT', payload: { field, direction } });
    },
    [dispatch],
  );

  const setViewMode = useCallback(
    (viewMode: ViewMode) => {
      dispatch({ type: 'SET_SHARED_VIEW_MODE', payload: viewMode });
    },
    [dispatch],
  );

  const testLocation = useCallback(
    async (locationName: string): Promise<TestConnectivityResponse> => {
      try {
        return await testLocationConnectivity(apiContext, locationName);
      } catch {
        addError({ id: 'test-location-error', message: 'Failed to test location connectivity', type: 'error' });
        throw new Error('Failed to test location connectivity');
      }
    },
    [addError, apiContext],
  );

  const pagination = state.locationsPagination;
  const totalPages = Math.ceil(pagination.totalSize / pagination.pageSize);
  const hasNextPage = !!pagination.nextPageToken;
  const hasPreviousPage = pagination.currentPage > 1;

  return {
    locations: state.locations,
    isLoading: state.locationsLoading,
    currentLocation: state.currentLocation,
    isLoadingLocation: state.locationLoading,
    allCredentials: state.allCredentials,
    nextPageToken: pagination.nextPageToken,
    currentPage: pagination.currentPage,
    totalSize: pagination.totalSize,
    pageSize: pagination.pageSize,
    filter: pagination.filter,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    fetchLocations,
    fetchLocation,
    clearCurrentLocation,
    setPageSize,
    setFilter,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    setSort,
    sortField: state.locationsSortField,
    sortDirection: state.locationsSortDirection,
    setViewMode,
    viewMode: state.sharedViewMode,
    testLocation,
    activeSearchName: state.locationsActiveSearchName,
    setActiveSearchName: (name: string | undefined) => dispatch({ type: 'SET_LOCATIONS_ACTIVE_SEARCH', payload: name }),
  };
};

// ==================== Credentials Hooks ====================

export const useCredentialsState = () => {
  const { state, dispatch } = useFileDeliveryContext();
  const { addError } = useErrorState();
  const apiContext = useApiContext();

  const orderBy =
    state.credentialsSortField && state.credentialsSortDirection
      ? `${state.credentialsSortField} ${state.credentialsSortDirection}`
      : undefined;

  const fetchCredentials = useCallback(
    async (params?: PaginationParams) => {
      dispatch({ type: 'FETCH_CREDENTIALS_START' });
      try {
        const response = await listCredentials(apiContext, {
          pageSize: state.credentialsPagination.pageSize,
          pageToken: state.credentialsPagination.currentPageToken,
          filter: state.credentialsPagination.filter,
          orderBy,
          ...params,
        });

        dispatch({
          type: 'FETCH_CREDENTIALS_SUCCESS',
          payload: {
            credentials: response.credentials,
            nextPageToken: response.nextPageToken,
            totalSize: response.totalSize,
          },
        });
      } catch {
        dispatch({ type: 'FETCH_CREDENTIALS_ERROR' });
        addError({ id: 'fetch-credentials-error', message: 'Failed to load credentials', type: 'error' });
      }
    },
    [
      dispatch,
      state.credentialsPagination.pageSize,
      state.credentialsPagination.currentPageToken,
      state.credentialsPagination.filter,
      orderBy,
      addError,
      apiContext,
    ],
  );

  const fetchCredential = useCallback(
    async (credentialId: string) => {
      dispatch({ type: 'FETCH_CREDENTIAL_START' });
      try {
        const credential = await getCredential(apiContext, credentialId);
        if (credential) {
          dispatch({ type: 'FETCH_CREDENTIAL_SUCCESS', payload: credential });
        } else {
          dispatch({ type: 'FETCH_CREDENTIAL_ERROR' });
          addError({ id: 'fetch-credential-error', message: 'Credential not found', type: 'error' });
        }
      } catch {
        dispatch({ type: 'FETCH_CREDENTIAL_ERROR' });
        addError({ id: 'fetch-credential-error', message: 'Failed to load credential', type: 'error' });
      }
    },
    [dispatch, addError, apiContext],
  );

  const clearCurrentCredential = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_CREDENTIAL' });
  }, [dispatch]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch({ type: 'SET_CREDENTIALS_PAGE_SIZE', payload: pageSize });
    },
    [dispatch],
  );

  const setFilter = useCallback(
    (filter: string | undefined) => {
      dispatch({ type: 'SET_CREDENTIALS_FILTER', payload: filter });
    },
    [dispatch],
  );

  const goToNextPage = useCallback(() => {
    dispatch({ type: 'CREDENTIALS_GO_TO_NEXT_PAGE' });
  }, [dispatch]);

  const goToPreviousPage = useCallback(() => {
    dispatch({ type: 'CREDENTIALS_GO_TO_PREVIOUS_PAGE' });
  }, [dispatch]);

  const goToFirstPage = useCallback(() => {
    dispatch({ type: 'CREDENTIALS_GO_TO_FIRST_PAGE' });
  }, [dispatch]);

  const setSort = useCallback(
    (field?: CredentialsSortField, direction?: SortDirection) => {
      dispatch({ type: 'SET_CREDENTIALS_SORT', payload: { field, direction } });
    },
    [dispatch],
  );

  const setViewMode = useCallback(
    (viewMode: ViewMode) => {
      dispatch({ type: 'SET_SHARED_VIEW_MODE', payload: viewMode });
    },
    [dispatch],
  );

  const pagination = state.credentialsPagination;
  const totalPages = Math.ceil(pagination.totalSize / pagination.pageSize);
  const hasNextPage = !!pagination.nextPageToken;
  const hasPreviousPage = pagination.currentPage > 1;

  return {
    credentials: state.credentials,
    isLoading: state.credentialsLoading,
    currentCredential: state.currentCredential,
    isLoadingCredential: state.credentialLoading,
    nextPageToken: pagination.nextPageToken,
    currentPage: pagination.currentPage,
    totalSize: pagination.totalSize,
    pageSize: pagination.pageSize,
    filter: pagination.filter,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    fetchCredentials,
    fetchCredential,
    clearCurrentCredential,
    setPageSize,
    setFilter,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    setSort,
    sortField: state.credentialsSortField,
    sortDirection: state.credentialsSortDirection,
    setViewMode,
    viewMode: state.sharedViewMode,
    activeSearchName: state.credentialsActiveSearchName,
    setActiveSearchName: (name: string | undefined) =>
      dispatch({ type: 'SET_CREDENTIALS_ACTIVE_SEARCH', payload: name }),
  };
};

// ==================== Schedules Hooks ====================

export const useSchedulesState = () => {
  const { state, dispatch } = useFileDeliveryContext();
  const { addError } = useErrorState();
  const apiContext = useApiContext();

  const orderBy =
    state.schedulesSortField && state.schedulesSortDirection
      ? `${state.schedulesSortField} ${state.schedulesSortDirection}`
      : undefined;

  const fetchSchedules = useCallback(
    async (params?: ListScheduledDeliveriesParams) => {
      dispatch({ type: 'FETCH_SCHEDULES_START' });
      try {
        const [schedulesResponse, locationsResponse] = await Promise.all([
          listScheduledDeliveries(apiContext, {
            pageSize: state.schedulesPagination.pageSize,
            pageToken: state.schedulesPagination.currentPageToken,
            filter: state.schedulesPagination.filter,
            orderBy,
            ...params,
          }),
          listLocations(apiContext),
        ]);

        const displaySchedules = schedulesResponse.scheduledDeliveries.map((schedule) =>
          transformScheduledDeliveryToDisplay(schedule, locationsResponse.locations, cronstrue.toString),
        );

        dispatch({
          type: 'FETCH_SCHEDULES_SUCCESS',
          payload: {
            schedules: displaySchedules,
            allLocations: locationsResponse.locations,
            nextPageToken: schedulesResponse.nextPageToken,
            totalSize: schedulesResponse.totalSize,
          },
        });
      } catch {
        dispatch({ type: 'FETCH_SCHEDULES_ERROR' });
        addError({ id: 'fetch-schedules-error', message: 'Failed to load schedules', type: 'error' });
      }
    },
    [
      dispatch,
      state.schedulesPagination.pageSize,
      state.schedulesPagination.currentPageToken,
      state.schedulesPagination.filter,
      orderBy,
      addError,
      apiContext,
    ],
  );

  const fetchSchedule = useCallback(
    async (scheduleId: string) => {
      dispatch({ type: 'FETCH_SCHEDULE_START' });
      try {
        const [schedule, locationsResponse] = await Promise.all([
          getScheduledDelivery(apiContext, scheduleId),
          listLocations(apiContext),
        ]);
        if (schedule) {
          const displaySchedule = transformScheduledDeliveryToDisplay(
            schedule,
            locationsResponse.locations,
            cronstrue.toString,
          );
          dispatch({
            type: 'FETCH_SCHEDULE_SUCCESS',
            payload: { schedule: displaySchedule, allLocations: locationsResponse.locations },
          });
        } else {
          dispatch({ type: 'FETCH_SCHEDULE_ERROR' });
          addError({ id: 'fetch-schedule-error', message: 'Schedule not found', type: 'error' });
        }
      } catch {
        dispatch({ type: 'FETCH_SCHEDULE_ERROR' });
        addError({ id: 'fetch-schedule-error', message: 'Failed to load schedule', type: 'error' });
      }
    },
    [dispatch, addError, apiContext],
  );

  const clearCurrentSchedule = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_SCHEDULE' });
  }, [dispatch]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch({ type: 'SET_SCHEDULES_PAGE_SIZE', payload: pageSize });
    },
    [dispatch],
  );

  const setFilter = useCallback(
    (filter: string | undefined) => {
      dispatch({ type: 'SET_SCHEDULES_FILTER', payload: filter });
    },
    [dispatch],
  );

  const goToNextPage = useCallback(() => {
    dispatch({ type: 'SCHEDULES_GO_TO_NEXT_PAGE' });
  }, [dispatch]);

  const goToPreviousPage = useCallback(() => {
    dispatch({ type: 'SCHEDULES_GO_TO_PREVIOUS_PAGE' });
  }, [dispatch]);

  const goToFirstPage = useCallback(() => {
    dispatch({ type: 'SCHEDULES_GO_TO_FIRST_PAGE' });
  }, [dispatch]);

  const setSort = useCallback(
    (field?: SchedulesSortField, direction?: SortDirection) => {
      dispatch({ type: 'SET_SCHEDULES_SORT', payload: { field, direction } });
    },
    [dispatch],
  );

  const setViewMode = useCallback(
    (viewMode: ViewMode) => {
      dispatch({ type: 'SET_SHARED_VIEW_MODE', payload: viewMode });
    },
    [dispatch],
  );

  const pagination = state.schedulesPagination;
  const totalPages = Math.ceil(pagination.totalSize / pagination.pageSize);
  const hasNextPage = !!pagination.nextPageToken;
  const hasPreviousPage = pagination.currentPage > 1;

  return {
    schedules: state.schedules,
    locations: state.allLocations,
    isLoading: state.schedulesLoading,
    currentSchedule: state.currentSchedule,
    isLoadingSchedule: state.scheduleLoading,
    allLocations: state.allLocations,
    nextPageToken: pagination.nextPageToken,
    currentPage: pagination.currentPage,
    totalSize: pagination.totalSize,
    pageSize: pagination.pageSize,
    filter: pagination.filter,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    fetchSchedules,
    fetchSchedule,
    clearCurrentSchedule,
    setPageSize,
    setFilter,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    setSort,
    sortField: state.schedulesSortField,
    sortDirection: state.schedulesSortDirection,
    setViewMode,
    viewMode: state.sharedViewMode,
    activeSearchName: state.schedulesActiveSearchName,
    setActiveSearchName: (name: string | undefined) => dispatch({ type: 'SET_SCHEDULES_ACTIVE_SEARCH', payload: name }),
  };
};

// ==================== Deliveries Hooks ====================

export const useDeliveriesState = () => {
  const { state, dispatch } = useFileDeliveryContext();
  const { addError } = useErrorState();
  const apiContext = useApiContext();

  const orderBy =
    state.deliveriesSortField && state.deliveriesSortDirection
      ? `${state.deliveriesSortField} ${state.deliveriesSortDirection}`
      : undefined;

  const fetchDeliveries = useCallback(
    async (params?: ListDeliveriesParams) => {
      dispatch({ type: 'FETCH_DELIVERIES_START' });
      try {
        const deliveriesResponse = await listDeliveries(apiContext, {
          pageSize: state.deliveriesPagination.pageSize,
          pageToken: state.deliveriesPagination.currentPageToken,
          filter: state.deliveriesPagination.filter,
          orderBy,
          ...params,
        });

        const displayDeliveries = deliveriesResponse.deliveries.map((delivery) =>
          transformDeliveryToDisplay(delivery, []),
        );

        dispatch({
          type: 'FETCH_DELIVERIES_SUCCESS',
          payload: {
            deliveries: displayDeliveries,
            allLocations: [],
            nextPageToken: deliveriesResponse.nextPageToken,
            totalSize: deliveriesResponse.totalSize,
          },
        });
      } catch {
        dispatch({ type: 'FETCH_DELIVERIES_ERROR' });
        addError({ id: 'fetch-deliveries-error', message: 'Failed to load deliveries', type: 'error' });
      }
    },
    [
      dispatch,
      state.deliveriesPagination.pageSize,
      state.deliveriesPagination.currentPageToken,
      state.deliveriesPagination.filter,
      orderBy,
      addError,
      apiContext,
    ],
  );

  const fetchDelivery = useCallback(
    async (deliveryId: string) => {
      dispatch({ type: 'FETCH_DELIVERY_START' });
      try {
        const delivery = await getDelivery(apiContext, deliveryId);
        if (delivery) {
          const displayDelivery = transformDeliveryToDisplay(delivery, []);
          dispatch({
            type: 'FETCH_DELIVERY_SUCCESS',
            payload: { delivery: displayDelivery, allLocations: [] },
          });
        } else {
          dispatch({ type: 'FETCH_DELIVERY_ERROR' });
          addError({ id: 'fetch-delivery-error', message: 'Delivery not found', type: 'error' });
        }
      } catch {
        dispatch({ type: 'FETCH_DELIVERY_ERROR' });
        addError({ id: 'fetch-delivery-error', message: 'Failed to load delivery', type: 'error' });
      }
    },
    [dispatch, addError, apiContext],
  );

  const clearCurrentDelivery = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_DELIVERY' });
  }, [dispatch]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch({ type: 'SET_DELIVERIES_PAGE_SIZE', payload: pageSize });
    },
    [dispatch],
  );

  const setFilter = useCallback(
    (filter: string | undefined) => {
      dispatch({ type: 'SET_DELIVERIES_FILTER', payload: filter });
    },
    [dispatch],
  );

  const goToNextPage = useCallback(() => {
    dispatch({ type: 'DELIVERIES_GO_TO_NEXT_PAGE' });
  }, [dispatch]);

  const goToPreviousPage = useCallback(() => {
    dispatch({ type: 'DELIVERIES_GO_TO_PREVIOUS_PAGE' });
  }, [dispatch]);

  const goToFirstPage = useCallback(() => {
    dispatch({ type: 'DELIVERIES_GO_TO_FIRST_PAGE' });
  }, [dispatch]);

  const setViewMode = useCallback(
    (viewMode: ViewMode) => {
      dispatch({ type: 'SET_SHARED_VIEW_MODE', payload: viewMode });
    },
    [dispatch],
  );

  const setSort = useCallback(
    (field?: DeliveriesSortField, direction?: SortDirection) => {
      dispatch({ type: 'SET_DELIVERIES_SORT', payload: { field, direction } });
    },
    [dispatch],
  );

  const pagination = state.deliveriesPagination;
  const totalPages = Math.ceil(pagination.totalSize / pagination.pageSize);
  const hasNextPage = !!pagination.nextPageToken;
  const hasPreviousPage = pagination.currentPage > 1;

  return {
    deliveries: state.deliveries,
    locations: state.allLocations,
    isLoading: state.deliveriesLoading,
    currentDelivery: state.currentDelivery,
    isLoadingDelivery: state.deliveryLoading,
    nextPageToken: pagination.nextPageToken,
    currentPage: pagination.currentPage,
    totalSize: pagination.totalSize,
    pageSize: pagination.pageSize,
    filter: pagination.filter,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    fetchDeliveries,
    fetchDelivery,
    clearCurrentDelivery,
    setPageSize,
    setFilter,
    setViewMode,
    viewMode: state.sharedViewMode,
    setSort,
    sortField: state.deliveriesSortField,
    sortDirection: state.deliveriesSortDirection,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    activeSearchName: state.deliveriesActiveSearchName,
    setActiveSearchName: (name: string | undefined) =>
      dispatch({ type: 'SET_DELIVERIES_ACTIVE_SEARCH', payload: name }),
  };
};

// ==================== Service Accounts Hooks ====================

export const useServiceAccountsState = () => {
  const { state, dispatch } = useFileDeliveryContext();
  const { addError } = useErrorState();
  const apiContext = useApiContext();

  const epadminContext: ApiContext = useMemo(
    () => ({
      datacenter: apiContext.datacenter,
      serviceAccount: 'epadmin',
    }),
    [apiContext.datacenter],
  );

  const orderBy =
    state.serviceAccountsSortField && state.serviceAccountsSortDirection
      ? `${state.serviceAccountsSortField} ${state.serviceAccountsSortDirection}`
      : undefined;

  const fetchAllServiceAccounts = useCallback(
    async (params?: ListServiceAccountsParams) => {
      dispatch({ type: 'FETCH_SERVICE_ACCOUNTS_START' });
      try {
        const response = await listServiceAccounts(epadminContext, {
          pageSize: state.serviceAccountsPagination.pageSize,
          pageToken: state.serviceAccountsPagination.currentPageToken,
          filter: state.serviceAccountsPagination.filter,
          orderBy,
          ...params,
        });
        dispatch({
          type: 'FETCH_SERVICE_ACCOUNTS_SUCCESS',
          payload: {
            accounts: response.serviceAccounts,
            nextPageToken: response.nextPageToken,
            totalSize: response.totalSize,
          },
        });
      } catch {
        dispatch({ type: 'FETCH_SERVICE_ACCOUNTS_ERROR' });
        addError({ id: 'fetch-accounts-error', message: 'Failed to load service accounts', type: 'error' });
      }
    },
    [
      dispatch,
      state.serviceAccountsPagination.pageSize,
      state.serviceAccountsPagination.currentPageToken,
      state.serviceAccountsPagination.filter,
      orderBy,
      addError,
      epadminContext,
    ],
  );

  const fetchAccount = useCallback(
    async (accountId: string) => {
      dispatch({ type: 'FETCH_SERVICE_ACCOUNT_START' });
      try {
        const account = await getServiceAccount(epadminContext, accountId);
        if (account) {
          dispatch({ type: 'FETCH_SERVICE_ACCOUNT_SUCCESS', payload: account });
        } else {
          dispatch({ type: 'FETCH_SERVICE_ACCOUNT_ERROR' });
          addError({ id: 'fetch-account-error', message: 'Service account not found', type: 'error' });
        }
      } catch {
        dispatch({ type: 'FETCH_SERVICE_ACCOUNT_ERROR' });
        addError({ id: 'fetch-account-error', message: 'Failed to load service account', type: 'error' });
      }
    },
    [dispatch, addError, epadminContext],
  );

  const clearCurrentAccount = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_SERVICE_ACCOUNT' });
  }, [dispatch]);

  const updatePassword = useCallback(
    async (accountName: string, newPassword: string) => {
      dispatch({ type: 'UPDATE_PASSWORD_START' });
      try {
        await rotateServiceAccountPassword(epadminContext, { name: accountName, newPassword });
        dispatch({ type: 'UPDATE_PASSWORD_SUCCESS' });
        return true;
      } catch {
        dispatch({ type: 'UPDATE_PASSWORD_ERROR' });
        addError({ id: 'update-password-error', message: 'Failed to update password', type: 'error' });
        return false;
      }
    },
    [dispatch, addError, epadminContext],
  );

  const clearSaveStatus = useCallback(() => {
    dispatch({ type: 'CLEAR_SAVE_STATUS' });
  }, [dispatch]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch({ type: 'SET_SERVICE_ACCOUNTS_PAGE_SIZE', payload: pageSize });
    },
    [dispatch],
  );

  const setFilter = useCallback(
    (filter: string | undefined) => {
      dispatch({ type: 'SET_SERVICE_ACCOUNTS_FILTER', payload: filter });
    },
    [dispatch],
  );

  const goToNextPage = useCallback(() => {
    dispatch({ type: 'SERVICE_ACCOUNTS_GO_TO_NEXT_PAGE' });
  }, [dispatch]);

  const goToPreviousPage = useCallback(() => {
    dispatch({ type: 'SERVICE_ACCOUNTS_GO_TO_PREVIOUS_PAGE' });
  }, [dispatch]);

  const goToFirstPage = useCallback(() => {
    dispatch({ type: 'SERVICE_ACCOUNTS_GO_TO_FIRST_PAGE' });
  }, [dispatch]);

  const setSort = useCallback(
    (field: ServiceAccountsSortField, direction: SortDirection) => {
      dispatch({ type: 'SET_SERVICE_ACCOUNTS_SORT', payload: { field, direction } });
    },
    [dispatch],
  );

  const setViewMode = useCallback(
    (viewMode: ViewMode) => {
      dispatch({ type: 'SET_SHARED_VIEW_MODE', payload: viewMode });
    },
    [dispatch],
  );

  const pagination = state.serviceAccountsPagination;
  const totalPages = Math.ceil(pagination.totalSize / pagination.pageSize);
  const hasNextPage = !!pagination.nextPageToken;
  const hasPreviousPage = pagination.currentPage > 1;

  return {
    accounts: state.serviceAccounts,
    isLoading: state.serviceAccountsLoading,
    currentAccount: state.currentServiceAccount,
    isLoadingAccount: state.serviceAccountLoading,
    isSaving: state.serviceAccountSaving,
    saveSuccess: state.serviceAccountSaveSuccess,
    nextPageToken: pagination.nextPageToken,
    currentPage: pagination.currentPage,
    totalSize: pagination.totalSize,
    pageSize: pagination.pageSize,
    filter: pagination.filter,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    fetchAllServiceAccounts,
    fetchAccount,
    clearCurrentAccount,
    updatePassword,
    clearSaveStatus,
    setPageSize,
    setFilter,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    setSort,
    sortField: state.serviceAccountsSortField,
    sortDirection: state.serviceAccountsSortDirection,
    setViewMode,
    viewMode: state.sharedViewMode,
    activeSearchName: state.serviceAccountsActiveSearchName,
    setActiveSearchName: (name: string | undefined) =>
      dispatch({ type: 'SET_SERVICE_ACCOUNTS_ACTIVE_SEARCH', payload: name }),
  };
};

// ==================== Service Account Selector Hook ====================

export const useServiceAccountSelector = () => {
  const { state, dispatch, serviceAccountSelectorRef } = useFileDeliveryContext();

  const setServiceAccount = useCallback(
    (value: string | undefined) => {
      dispatch({ type: 'SET_SELECTED_SERVICE_ACCOUNT', payload: value });
    },
    [dispatch],
  );

  const focusSelector = useCallback(() => {
    if (serviceAccountSelectorRef.current) {
      serviceAccountSelectorRef.current.focus();
      serviceAccountSelectorRef.current.click();
    }
  }, [serviceAccountSelectorRef]);

  const markInitialized = useCallback(() => {
    dispatch({ type: 'SET_SERVICE_ACCOUNT_INITIALIZED' });
  }, [dispatch]);

  return {
    serviceAccount: state.selectedServiceAccount,
    setServiceAccount,
    selectorRef: serviceAccountSelectorRef,
    focusSelector,
    isInitialized: state.serviceAccountInitialized,
    markInitialized,
  };
};

// ==================== Data Region Selector Hook ====================

export const useDataRegionSelector = () => {
  const { state, dispatch } = useFileDeliveryContext();

  const setDataRegion = useCallback(
    (value: string | undefined) => {
      dispatch({ type: 'SET_SELECTED_DATA_REGION', payload: value });
    },
    [dispatch],
  );

  const markInitialized = useCallback(() => {
    dispatch({ type: 'SET_DATA_REGION_INITIALIZED' });
  }, [dispatch]);

  return {
    dataRegion: state.selectedDataRegion,
    setDataRegion,
    isInitialized: state.dataRegionInitialized,
    markInitialized,
  };
};

// ==================== Context Change Handler Hook ====================

export const useContextChangeHandler = (onContextChange: () => void) => {
  const { state, dispatch } = useFileDeliveryContext();
  const prevDataRegionRef = useRef(state.selectedDataRegion);
  const prevServiceAccountRef = useRef(state.selectedServiceAccount);

  useEffect(() => {
    const dataRegionChanged = prevDataRegionRef.current !== state.selectedDataRegion;
    const serviceAccountChanged = prevServiceAccountRef.current !== state.selectedServiceAccount;

    if ((dataRegionChanged || serviceAccountChanged) && prevDataRegionRef.current !== undefined) {
      dispatch({ type: 'RESET_ALL_DATA' });
      onContextChange();
    }

    prevDataRegionRef.current = state.selectedDataRegion;
    prevServiceAccountRef.current = state.selectedServiceAccount;
  }, [state.selectedDataRegion, state.selectedServiceAccount, dispatch, onContextChange]);
};
